import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, Observable, combineLatest, from } from 'rxjs';
import { ApiService } from '../../api/api.service';
import { ProjectService } from '../project/project.service';
import { first, filter, take, tap } from 'rxjs/operators';
import { SubmoduleService } from '../submodule/submodule.service';
import { ProjectElement, ProjectElementResponse } from './project-element.interface';
import { get } from 'lodash-es';
import { TranslateService } from '../../utilities/translate/translate.service';
import { ProjectSubmodule } from '../submodule/project-submodule.interface';
import { BasicAreaService } from '../basic-area/basic-area.service';
import { RouterService } from '../../utilities/router/router.service';
import { ElementCommentSummary, ElementChange, ElementComment } from '../log/logs.interface';
import { ModuleService } from '../module/module.service';
import { ToastService } from '../../services/toast/toast.service';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

/**
 * Element core service.
 *
 * Contains all the private logic for Element management.
 *
 * **Only ElementService is allowed to access this Service.**
 */
@Injectable({
  providedIn: 'root'
})
export class ElementCoreService {

  /** Element comment counts subject. */
  protected _counts: Subject<ElementCommentSummary[]> = new BehaviorSubject<ElementCommentSummary[]>(null);

  /** Element comment counts observable. */
  public count$ = this._counts.asObservable();

  /** Element subject. */
  protected _elements: Subject<ProjectElement[]> = new BehaviorSubject<ProjectElement[]>(null);

  /** Element observable. */
  public element$ = this._elements.asObservable();

  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    protected projectService: ProjectService,
    protected routerService: RouterService,
    protected submoduleService: SubmoduleService,
    protected toastService: ToastService,
    protected translateService: TranslateService
  ) {
    this._trackSubmodules();
  }


  /**
   * Get elements.
   *
   * @returns The elements in current project.
   */
  public get(): ProjectElement[] {
    let elements: ProjectElement[];
    this.element$.pipe(first()).subscribe(s => elements = s);
    return elements;
  }

  /**
   * Get specific element by id.
   *
   * Because it's possible there are multiple (valid) instances of elements with the same id,
   * an optional set of properties/values can be used to pick the specific element from the store.
   * For example, there can be multiple copies of a basic area requirement, all with the same id.
   *
   * @param id The id of the element.
   * @returns The requested element.
   */
  public getElement(id: ProjectElement['_id'], values: Partial<ProjectElement> = {}): ProjectElement {
    let elements = this.get().filter(e => e.active && e._id === id);
    Object.keys(values).forEach(prop => {
      elements = elements.filter(e => e[prop] === values[prop]);
    });
    if (elements.length > 1) {
      console.warn('More than one element found: ', elements);
    }
    return elements[0];
  }

  /**
   * Get specific element by code.
   *
   * @param id The code of the element.
   * @returns The requested element.
   */
  public getElementByCode(code: ProjectElement['code']): ProjectElement {
    return this.get().find(e => e.code === code);
  }

  /**
   * Get specific element by identifier.
   *
   * @param id The identifier of the element.
   * @returns The requested element.
   */
  public getElementByIdentifier(identifier: ProjectElement['identifier']): ProjectElement {
    return this.get().find(e => e.identifier === identifier);
  }

  /**
   * Add new element.
   *
   * @todo Move to helper Class `ElementCreateService`.
   *
   * @param template Template to base the new element on.
   * @returns Observable of the newly created element.
   */
  public add(template: Partial<ProjectElement>): Observable<Partial<ProjectElement>> {
    return new Observable(observer => {
      this._create({
        ...template,
        active: true,
        assignedToUserId: template.assignedToUserId || -1,
      }).subscribe(results => {
        observer.next(results);
        observer.complete();
        this.toastService.open(this.translateService.get('specificationAdded'));
      });
    });
  }

  /**
   * Add empty paragraph element to document.
   *
   * @todo Move to helper Class `ElementCreateService`.
   * @todo Should `projectLevel` always be 1?
   *
   * @param submodule The parent submodule.
   * @returns Observable of the newly created element.
   */
  public addParagraph(submodule: Partial<ProjectSubmodule>): Observable<Partial<ProjectElement>> {
    return this._create({
      active: true,
      assignedToUserId: -1,
      constructionId: get(submodule, 'elements[0].constructionId', this.projectService.getId()),
      hasInputInstruction: false,
      hasMultipleQualityLevels: false,
      isCustom: true,
      inputType: 'Long String',
      knowledgeLevel: 1,
      moduleId: submodule.moduleId,
      name: this.translateService.get('newSpecification'),
      projectLevel: 1,
      submoduleId: submodule._id,
      value: `<p>${ this.translateService.get('newSpecificationValue') }</p>`,
    });
  }

  /**
   * Add new requirement based on basic area values.
   *
   * @note When an *edited* basic area requirement is used as template for a new requirement,
   *  an errors occurs because of the inherited `changes` property.
   *  For now, just delete this property before merging to prevent any errors.
   *
   * @todo Move to helper Class `ElementCreateService`.
   *
   * @param template Template values which should be inherited by the new requirement.
   * @param config Optional values
   * @returns Observable of the newly created element.
   */
  public addRequirement(template: ProjectElement, config?: any): Observable<Partial<ProjectElement>> {

    // Delete changes from template to prevent API errors
    delete template.changes;

    // If no config parameters have been set
    const constructionId = (typeof config !== 'undefined' && config.hasOwnProperty('customValues')
      && typeof config.customValues !== 'undefined') && config.customValues.hasOwnProperty('constructionId')
      ? config.customValues.constructionId
      : this.routerService.isActive('area', true) as string;

    return new Observable(observer => {
      this._create({
        ...template,
        assignedToUserId: -1,
        basicAreaElementId: template._id,
        basicAreaValue: template.originalValue,
        constructionId: constructionId,
        hasOwnValue: false,
        isCustom: false,
        model: null,
        projectLevel: 5
      }).subscribe(results => {
        observer.next(results);
        observer.complete();
        this.toastService.open(this.translateService.get('specificationCopied'));
      });
    });
  }

  /**
   * Check if the element is edited.
   *
   * The check is different for each type of element, so a unique check per element type
   * can be used. The check is based on current and template/original value. Checking
   * based on `element.changes` length can give false positives.
   *
   * @note When (a lot) more element types need to be checked for difference, it's maybe
   * better to create a seperate service for difference checking. For now, it's fine.
   *
   * @param element The element to check.
   * @returns Whether the given element is edited. Fallback to `false` to prevent unwanted
   *  actions added to the menu.
   */
  public isEdited(element: ProjectElement): boolean {

    // Template elements.
    if (element.templateValues && element.templateValues.length) {
      // Project and Building
      if ([1, 2].indexOf(element.projectLevel) > -1) {
        const template = element.templateValues.find(tv => tv.quality === element.quality);
        return template.value !== element.value;
      }
    }

    // Custom elements.
    if (element.isCustom && element.isCustom === true) {
      // Project and Building
      if ([1, 2].indexOf(element.projectLevel) > -1) {
        return false;
      }
    }

    return false;
  }

  /**
   * Check whether the element is of a certain type.
   *
   * @todo There seems to be a bug, where not al custom elements have projectLevel 5!
   *  Checking for projectLevel anyway now, but keep commented code until sure it works well.
   *
   * @param element The element to check.
   * @param type The requested type.
   * @returns Whether the element is of the requested type.
   */
  public isOfType(element: ProjectElement, type: 'area' | 'basicArea' | 'basicAreaCopy' | 'building' | 'project'): boolean {
    switch (true) {
      case element.projectLevel === 1:
        return type === 'project';
      case element.projectLevel === 2:
        return type === 'building';
      case element.projectLevel === 4:
        return type === 'basicArea';
      case element.projectLevel === 5 && typeof element.basicAreaValue !== 'undefined':
        return type === 'basicAreaCopy';
      case element.projectLevel === 5:
        return type === 'area';
    }
  }

  /**
   * Resolve comment counts.
   *
   * @note The results are saved locally in the element service to prevent
   *  circular errors. All subsequent counts should be done by the log service.
   *
   * @note The Observable is returned as a Promise, so it can be used with the
   *  AngularJS ui-router. Remove conversion after migration to Angular Router.
   *
   * @param projectId The id of the project.
   * @returns Promise with comment count summaries.
   */
  public resolveCommentCounts(projectId: Project['_id']): Promise<ElementCommentSummary[]> {
    return new Observable(observer => {
      this.apiService.getElementCommentCount().subscribe(counts => {
        this._counts.next(counts.summary);
        observer.next(counts.summary);
        observer.complete();
      });
    }).toPromise() as Promise<ElementCommentSummary[]>;
  }

  /**
   * Create new element.
   *
   * @param element The template/base values for the new element.
   * @returns The created element as returned from the API.
   */
  protected _create(element: Partial<ProjectElement>): Observable<Partial<ProjectElement>> {
    return new Observable(observer => {
      this.apiLegacyService.createElement(element).subscribe(response => {
        this.projectService.addElement({ ...element, ...response.element, changes: [] });
        observer.next(response.element);
        observer.complete();
        // this.toastService.open(this.translateService.get('specificationadded'));
      });
    });
  }

  /**
   * Patch element.
   *
   * Sets a new value for element properties. Input can either be one or multiple values.
   * After patching the element in de database, the editted element is also inserted into
   * the local project datastore.
   *
   * When the given element isn't actually an element, but a template value from a basic
   * area, create a *new* element instead of patching the basic area values.
   *
   * @todo In the patch method, complete the observer *after* the patching is done, and
   *  return the *full* element instead of the response.
   *
   * @param element The element to patch.
   * @param values The updated values
   * @param [config] Optional config object.
   * @returns Observable with state.
   */
  protected _patchElement(element: ProjectElement, values: Partial<ProjectElement>, config?: any): Observable<Partial<ProjectElement>> {

    // Copy the element when it's a basic area requirement and matches the correct routes.
    if (this._shouldCreateCopy(element)) {
      return new Observable(observer => {
        this.addRequirement({ ...element, ...values }, config).subscribe((response) => {
          observer.next(response);
          observer.complete();
        });
      });
    }

    // Explicitly add constructionId when dealing with a requirement based on a copy.
    if (element.basicAreaElementId && !values.constructionId) {
      values.constructionId = element.constructionId;
    }

    // When the `isCustom` property is available, mark it as true.
    // Remove when sure it isn't needed anymore.
    // if (element.hasOwnProperty('isCustom')) {
    //   values.isCustom = true;
    // }

    // Patch element.
    return new Observable(observer => {
      this.apiLegacyService.patchElement(element, values).subscribe(response => {
        this.projectService.patchElement(element, { ...values, ...response.element });
        observer.next(response.element);
        observer.complete();
      });
    });
  }

  /**
   * Whether to create a copy of the template instead of patching it.
   *
   * Since there isn't any clear method to 'guess' whether there should
   * be a copy or a patch, there should be a way to manually set it. To
   * prevent adding a config param to *every* possible element patch
   * method, the routes where a *copy* should occur, can be added here.
   *
   * @note This is quiet 'dirty', but it's fast/convenient.
   *  However **it breaks when updating state names**.
   *
   * @todo This should be determined in the route config files, but doing
   *  so right now, can be a pain with AngularJS router. Should be done
   *  after migrating to Angular Router.
   *
   * @param element The element to check.
   * @returns Whether the template should be copied instead of patched.
   */
  protected _shouldCreateCopy(element: ProjectElement): boolean {
    return element.projectLevel === 4 && [
      'project/area',
      'project/areas/matrix'
    ].indexOf(this.routerService.getState()) > -1;
  }

  /**
   * Track submodules and set elements.
   *
   * The elements are merged with the comment counts query results.
   *
   * @todo Comment count query should be done in resolver, before
   *  loading project.
   */
  protected _trackSubmodules(): void {
    this.submoduleService.submodule$.pipe(
      filter(submodules => submodules !== null)
    ).subscribe(submodules => {
      const elements: ProjectElement[] = [];
      submodules.forEach(submodule => {
        elements.push(...submodule.elements);
      });
      this.count$.pipe(take(1)).subscribe(counts => {
        if (counts) {
          for (const comment of counts) {
            if (elements.find(e => e._id === comment.elementId)) {
              elements.find(e => e._id === comment.elementId).commentCount = comment.count;
            }
          }
        }
        this._elements.next(elements);
      });
    });
  }
}
